import React, { ChangeEvent, MouseEvent, FormEvent, FC } from "react";

import { FileRejection } from "react-dropzone";
import { toast } from "react-toastify";

import { FormEmployeeSurveyQuestionType, useBoolean } from "~/hooks";

import {
  Form,
  Button,
  ButtonWithIcon,
  NumberField,
  TextField,
  DropzoneFileFieldWithFile,
} from "~/components/atoms";
import {
  EditAndFormSubmitButton,
  LabelWithTextField,
  FolderIconWithTitle,
  LabelWithDropDownField,
  LabelWithCheckBox,
} from "~/components/molecules";

import {
  CheckBoxType,
  OptionType,
  QuestionTypes,
  RadioButtonType,
  SelectBoxType,
  SingleValueType,
} from "~/domains";

import {
  DOCUMENT_FILE_AVAILABLE_TYPE,
  DOCUMENT_FILE_MAX_BYTE_SIZE,
} from "~/constants/file";

type PropsType = {
  isSubmitting: boolean;
  defaultAccordionOpen: boolean;
  employeeSurveyQuestion: FormEmployeeSurveyQuestionType;
  onChangeQuestion: (newValue: string) => void;
  onChangeFile: (newValue: File | undefined) => void;
  onChangeQuestionType: (newValue: SingleValueType<OptionType>) => void;
  addEmployeeSurveyQuestionOption: () => void;
  removeEmployeeSurveyQuestionOption: (index: number) => void;
  onChangeEmployeeSurveyQuestionOptionText: (
    index: number,
    newValue: string,
  ) => void;
  onChangeEmployeeSurveyQuestionRequired: (newValue: boolean) => void;
  onChangeEmployeeSurveyQuestionOptionPoint: (
    index: number,
    newValue: number,
  ) => void;
  removeEmployeeSurveyQuestion: () => void;
  saveEmployeeSurveyQuestion: () => void;
  onChangeSetNotReadOnly: () => void;
  onChangeSetReadOnly: () => void;
  handleDuplicateEmployeeSurveyQuestion: () => void;
};

export const EmployeeSurveyQuestionForm: FC<PropsType> = ({
  isSubmitting,
  defaultAccordionOpen,
  employeeSurveyQuestion,
  removeEmployeeSurveyQuestion,
  saveEmployeeSurveyQuestion,
  onChangeQuestion,
  onChangeFile,
  onChangeQuestionType,
  addEmployeeSurveyQuestionOption,
  removeEmployeeSurveyQuestionOption,
  onChangeEmployeeSurveyQuestionOptionText,
  onChangeEmployeeSurveyQuestionOptionPoint,
  onChangeEmployeeSurveyQuestionRequired,
  onChangeSetNotReadOnly,
  onChangeSetReadOnly,
  handleDuplicateEmployeeSurveyQuestion,
}: PropsType) => {
  const accordionOpen = useBoolean(defaultAccordionOpen);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    saveEmployeeSurveyQuestion();
  };

  const handleNotReadonly = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    onChangeSetNotReadOnly();
  };

  const handleRemove = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const isConfirm = confirm("本当に削除しますか？");
    if (!isConfirm) return;

    removeEmployeeSurveyQuestion();
  };

  const handleAttachFileChange = (
    acceptedFile: File[],
    fileRejections: FileRejection[],
  ) => {
    if (fileRejections.length) {
      fileRejections.map(({ file, errors }) => {
        if (!errors[0]) return;
        toast.error(`${file.name} - ${errors[0].message}`);
      });
      return;
    }
    acceptedFile[0] && onChangeFile(acceptedFile[0]);
  };

  const accordionToggle = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    accordionOpen.toggle();
  };

  const dropzoneClassName = `${
    employeeSurveyQuestion.isReadOnly
      ? "bg-secondary-300"
      : "bg-primary-200 cursor-pointer"
  } rounded-3xl text-gray-500 flex justify-center items-center py-2 px-2`;

  const canAddQuestionOption =
    RadioButtonType.id === employeeSurveyQuestion.questionType.id ||
    SelectBoxType.id === employeeSurveyQuestion.questionType.id ||
    CheckBoxType.id === employeeSurveyQuestion.questionType.id;

  return (
    <Form className="space-y-4" onSubmit={onSubmit}>
      <FolderIconWithTitle
        title={employeeSurveyQuestion.question}
        onClick={accordionToggle}
      />
      {accordionOpen.isChecked && (
        <div className="space-y-6 ml-9">
          <LabelWithTextField
            labelText="項目"
            type="text"
            name="name"
            placeholder="項目"
            required
            value={employeeSurveyQuestion.question}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              onChangeQuestion(e.target.value)
            }
            disabled={employeeSurveyQuestion.isReadOnly}
          />
          <DropzoneFileFieldWithFile
            readonly={employeeSurveyQuestion.isReadOnly}
            label="画像"
            maxFileByte={DOCUMENT_FILE_MAX_BYTE_SIZE}
            acceptFileTypes={DOCUMENT_FILE_AVAILABLE_TYPE}
            onDropFiles={handleAttachFileChange}
            onRemoveFile={() => onChangeFile(undefined)}
            fileClassName={dropzoneClassName}
            fileName={employeeSurveyQuestion.file?.name || ""}
            noFilesText="JPEG / PNG"
          />
          {employeeSurveyQuestion.file?.url && (
            <img
              src={employeeSurveyQuestion.file.url}
              alt="画像"
              className="w-1/2 mx-auto"
            />
          )}
          <LabelWithDropDownField
            labelText="回答の種類"
            required
            name="questionTypeId"
            isDisabled={employeeSurveyQuestion.isReadOnly}
            options={QuestionTypes.map((type) => ({
              value: type.id.toString(),
              label: type.name,
            }))}
            value={{
              value: employeeSurveyQuestion.questionType.id.toString(),
              label: employeeSurveyQuestion.questionType.name,
            }}
            onChange={onChangeQuestionType}
          />
          {canAddQuestionOption && (
            <div className="space-y-4">
              {employeeSurveyQuestion.employeeSurveyQuestionOptions?.map(
                (option, index) => (
                  <div key={index} className="flex space-x-4">
                    <ButtonWithIcon
                      onClick={() => removeEmployeeSurveyQuestionOption(index)}
                      icon={{
                        icon: "ioCloseOutline",
                        size: "1.5rem",
                      }}
                      srOnlyText="削除"
                      disabled={employeeSurveyQuestion.isReadOnly}
                    />
                    <TextField
                      type="text"
                      name="option"
                      placeholder="選択肢"
                      onlyBottomBorder
                      required
                      value={option.option}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        onChangeEmployeeSurveyQuestionOptionText(
                          index,
                          e.target.value,
                        )
                      }
                      disabled={employeeSurveyQuestion.isReadOnly}
                      className="w-[80%]"
                    />
                    <NumberField
                      className="w-[20%]"
                      name="point"
                      placeholder="ポイント"
                      required
                      value={option.point}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        onChangeEmployeeSurveyQuestionOptionPoint(
                          index,
                          parseInt(e.target.value, 10),
                        )
                      }
                      disabled={employeeSurveyQuestion.isReadOnly}
                    />
                  </div>
                ),
              )}
              <div className="flex justify-end">
                <Button
                  text="選択肢を追加"
                  onClick={addEmployeeSurveyQuestionOption}
                  color="gray"
                  outline
                  readonly={employeeSurveyQuestion.isReadOnly}
                />
              </div>
            </div>
          )}
          <LabelWithCheckBox
            labelText="必須"
            name="required"
            className="justify-end flex"
            disabled={employeeSurveyQuestion.isReadOnly}
            checked={employeeSurveyQuestion.required}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              onChangeEmployeeSurveyQuestionRequired(e.target.checked);
            }}
          />
          <EditAndFormSubmitButton
            isSubmitting={isSubmitting}
            isReadOnly={employeeSurveyQuestion.isReadOnly}
            handleNotReadonly={handleNotReadonly}
            handleReadonly={onChangeSetReadOnly}
            handleRemove={handleRemove}
            handleDuplicate={handleDuplicateEmployeeSurveyQuestion}
          />
        </div>
      )}
    </Form>
  );
};
